import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentUserId,
  getDealApplicationId,
  getIsInterimDashboard,
  getNhDealApplicationId,
  getTaskCount,
  setTaskCount,
  TaskFilterType,
} from "features/authSlice";
import {
  NeedItem,
  useCreateDashboardSessionMutation,
  useCreateNeedsMutation,
  useGetDashboardItemsQuery,
  useGetNeedsQuery,
} from "pages/dashboard/home/dashboardService";
import { getSessionHeldUnderReviewIds } from "pages/dashboard/store/dashboardSlice";
import {
  DASHBOARD_CREATE_NEEDS_TRIGGERED,
  DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE,
  DASHBOARD_SESSION_ID,
  DASHBOARD_SESSION_START_TIMESTAMP,
} from "utilities/Constant";
import { getEffectiveTaskStatusForCount } from "utilities/needsHelper";

const getDashboardSessionStorageKey = (userId: string, nhDealApplicationId: string) =>
  `dashboardSession_${userId}_${nhDealApplicationId}`;

const buildDashboardSession = (): DashboardSession => {
  let dashboardSessionId = sessionStorage.getItem(DASHBOARD_SESSION_ID);
  let dashboardSessionStartTimestamp = sessionStorage.getItem(DASHBOARD_SESSION_START_TIMESTAMP);

  if (!dashboardSessionId) {
    dashboardSessionId = crypto.randomUUID();
    dashboardSessionStartTimestamp = new Date().toISOString();
    sessionStorage.setItem(DASHBOARD_SESSION_ID, dashboardSessionId);
    sessionStorage.setItem(DASHBOARD_SESSION_START_TIMESTAMP, dashboardSessionStartTimestamp);
  }

  return {
    dashboardSessionId,
    dashboardSessionStartTimestamp: dashboardSessionStartTimestamp ?? new Date().toISOString(),
    dashboardSessionEndTimestamp: null,
  };
};

interface DashboardSession {
  dashboardSessionId: string;
  dashboardSessionStartTimestamp: string | null;
  dashboardSessionEndTimestamp: string | null;
}

interface DashboardNeedsOrchestrationResult {
  needsList: NeedItem[];
  isNeedsListLoading: boolean;
  isNeedsListFetching: boolean;
  needsListError: boolean;
  refetchNeedsList: () => Promise<{ data?: NeedItem[] } | undefined>;
  isCreatingNeeds: boolean;
  isDashboardItemsLoading: boolean;
}

export function useDashboardNeedsOrchestration(): DashboardNeedsOrchestrationResult {
  const dispatch = useDispatch();
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const isInterimDashboard = useSelector(getIsInterimDashboard);
  const sidebarTaskCount = useSelector(getTaskCount);
  const sessionHeldUnderReviewIds = useSelector(getSessionHeldUnderReviewIds);

  const [needsList, setNeedsList] = useState<NeedItem[]>([]);
  const [isTaskCountSet, setIsTaskCountSet] = useState(false);
  const [shouldFetchNeedsList, setShouldFetchNeedsList] = useState(false);
  const [isDashboardSessionReady, setIsDashboardSessionReady] = useState(false);
  const [createDashboardSession] = useCreateDashboardSessionMutation();

  const { data: dashboardData, isLoading: isDashboardItemsLoading } = useGetDashboardItemsQuery(
    { dealApplicationId: nhDealApplicationId, userId },
    { skip: !nhDealApplicationId || !userId },
  );
  const [createNeeds, { isLoading: isCreatingNeeds, isSuccess: isCreateNeedsSucceeded }] = useCreateNeedsMutation();

  useEffect(() => {
    if (!userId || !nhDealApplicationId) {
      return;
    }

    const sessionKey = getDashboardSessionStorageKey(userId, nhDealApplicationId);
    if (sessionStorage.getItem(sessionKey) === "true") {
      setIsDashboardSessionReady(true);
      return;
    }

    void createDashboardSession({
      nhDealApplicationId,
      userId,
      dashboardSession: buildDashboardSession(),
    })
      .unwrap()
      .then(() => {
        sessionStorage.setItem(sessionKey, "true");
        setIsDashboardSessionReady(true);
      })
      .catch((error: unknown) => {
        console.error("Failed to create dashboard session:", error);
        setIsDashboardSessionReady(true);
      });
  }, [userId, nhDealApplicationId, createDashboardSession]);

  useEffect(() => {
    if (!isDashboardSessionReady || !dashboardData || isCreatingNeeds) return;

    const hasCreateNeedsTriggeredForSession =
      sessionStorage.getItem(DASHBOARD_CREATE_NEEDS_TRIGGERED) === DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE;
    const shouldEvaluateNeedsCreation =
      (dashboardData?.isAwaitingNeedsCreation === true ||
        (isInterimDashboard && dashboardData?.isNew && dashboardData?.isCoborrowerNew)) &&
      !hasCreateNeedsTriggeredForSession;

    if (shouldEvaluateNeedsCreation) {
      if (!dashboardData?.isApplicationFormRequired) {
        setShouldFetchNeedsList(true);
      } else if (!isCreateNeedsSucceeded && !shouldFetchNeedsList) {
        void createNeeds({ dealApplicationId, userId, isInterimDashboard });
        sessionStorage.setItem(DASHBOARD_CREATE_NEEDS_TRIGGERED, DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE);
      }
    } else {
      setShouldFetchNeedsList(true);
    }
  }, [
    dashboardData?.isNew,
    dashboardData?.isCoborrowerNew,
    dashboardData?.isAwaitingNeedsCreation,
    dashboardData?.isApplicationFormRequired,
    isCreatingNeeds,
    isInterimDashboard,
    isCreateNeedsSucceeded,
    shouldFetchNeedsList,
    createNeeds,
    dealApplicationId,
    userId,
    isDashboardSessionReady,
  ]);

  useEffect(() => {
    if (isCreateNeedsSucceeded && !shouldFetchNeedsList) {
      setShouldFetchNeedsList(true);
    }
  }, [isCreateNeedsSucceeded, shouldFetchNeedsList]);

  const needsQueryResult = useGetNeedsQuery(
    { nhDealApplicationId, userId },
    {
      skip: isCreatingNeeds || !shouldFetchNeedsList || !isDashboardSessionReady,
      refetchOnMountOrArgChange: false,
      selectFromResult: ({ data, isLoading, isFetching, error }) => ({
        data,
        isLoading,
        isFetching,
        error,
      }),
    },
  );

  const { data: needItems, isLoading: isNeedsListLoading, isFetching: isNeedsListFetching, error: needsListError } =
    needsQueryResult;

  const refetchNeedsList = async (): Promise<{ data?: NeedItem[] } | undefined> => {
    const result = await needsQueryResult.refetch();
    return { data: result.data };
  };

  useEffect(() => {
    if (!isNeedsListLoading && !needsListError && needItems) {
      setNeedsList(needItems);

      const matchingNeeds = needItems.filter(
        (item) => getEffectiveTaskStatusForCount(item, sessionHeldUnderReviewIds) === TaskFilterType.ToDo,
      ).length;
      if (!isTaskCountSet || sidebarTaskCount !== matchingNeeds) {
        dispatch(setTaskCount(matchingNeeds));
        setIsTaskCountSet(true);
      }
    }
  }, [needItems, isNeedsListLoading, needsListError, sessionHeldUnderReviewIds]);

  return {
    needsList,
    isNeedsListLoading,
    isNeedsListFetching,
    needsListError: Boolean(needsListError),
    refetchNeedsList,
    isCreatingNeeds,
    isDashboardItemsLoading,
  };
}
